import { Briefcase, Scale, ShieldAlert, MessagesSquare, Sparkles, BadgeCheck, Handshake, Users } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  useSeoMeta, LandingHero, CoverageGrid, WhyUsSection,
  TestimonialsSection, FaqSection, Breadcrumb,
} from "../lib/landingShared";

const HERO_IMG = "https://images.pexels.com/photos/7691679/pexels-photo-7691679.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700";

const COVERAGE = [
  { icon: Briefcase,      title: "Responsabilità civile professionale", desc: "Tutela patrimoniale per danni economici causati a clienti o terzi nell'esercizio dell'attività." },
  { icon: ShieldAlert,    title: "Errori, omissioni, negligenza",        desc: "Indennizzo per richieste di risarcimento legate a errori professionali, anche pregressi." },
  { icon: Scale,          title: "Tutela legale",                         desc: "Spese di difesa in controversie civili, penali, amministrative e fiscali." },
  { icon: MessagesSquare, title: "Consulenza dedicata",                   desc: "Un colloquio per capire categoria, fatturato e rischi reali della tua attività." },
];

const WHY = [
  { icon: Handshake,  title: "Un form per iniziare, una consulenza per scegliere",  desc: "Il preventivatore raccoglie le informazioni essenziali; poi capiamo insieme cosa fai e quali sono i tuoi rischi reali." },
  { icon: Sparkles,   title: "Garanzie su misura",         desc: "Massimali, retroattività e franchigie calibrate sulla dimensione dello studio o dell'impresa." },
  { icon: BadgeCheck, title: "Chiarezza sulle clausole",   desc: "Cosa è incluso, cosa è escluso e cosa cambia in caso di sinistro: te lo spieghiamo prima della firma." },
  { icon: Users,      title: "Più compagnie a confronto",  desc: "Confrontiamo le condizioni delle principali compagnie del settore, scegliamo insieme." },
];

const FAQS = [
  { q: "A chi serve la polizza RC Professionale?",
    a: "A tutti i professionisti che, nell'esercizio della propria attività, possono causare un danno economico a clienti o terzi: avvocati, commercialisti, consulenti del lavoro, architetti, ingegneri, geometri, notai, medici, psicologi, consulenti finanziari, IT consultant, agenti immobiliari, traduttori, ecc. Per molte categorie è obbligatoria per legge." },
  { q: "Cosa copre esattamente la RC Professionale?",
    a: "Copre la responsabilità civile derivante da errori, omissioni, negligenza, imprudenza o imperizia commessi nell'esercizio dell'attività professionale: l'assicurazione risarcisce il cliente danneggiato e si fa carico delle spese legali. Le polizze di qualità includono retroattività (copre anche fatti pregressi) e ultrattività (copre fatti scoperti dopo la cessazione dell'attività)." },
  { q: "Quanto costa una polizza RC Professionale?",
    a: "Il premio dipende dalla categoria professionale, dal fatturato, dal massimale scelto, da eventuali sinistri pregressi, dalla retroattività richiesta. Indicativamente, per un libero professionista con fatturato medio si parte da 200-400 € all'anno per massimali standard. Categorie con rischio più elevato (sanitarie, finanziarie, ingegneria) hanno premi superiori." },
  { q: "Posso integrare la tutela legale?",
    a: "Sì, e per molte attività è altamente consigliato. La tutela legale copre le spese di difesa anche in controversie non strettamente legate alla responsabilità civile: contenziosi col fisco, recupero crediti, contestazioni amministrative. Si aggiunge come garanzia opzionale alla RC Professionale o come polizza autonoma." },
];

const TRUST = [
  { value: "1958", label: "Anno di fondazione" },
  { value: "3",    label: "Generazioni di esperienza" },
  { value: "24h",  label: "Risposta alla richiesta" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
};

export default function RcProfessionale() {
  useSeoMeta({
    title: "RC Professionale — Assicurazione per professionisti | Da Pra Assicurazioni",
    description: "Polizza RC Professionale su misura per professionisti, consulenti e piccole imprese. Consulenza dedicata, chiarezza sulle clausole e confronto tra compagnie selezionate.",
    keywords: "RC professionale, assicurazione professionale, polizza professionisti, polizza errori professionali, tutela legale professionisti",
    canonical: "https://dapraassicurazioni.it/rc-professionale",
    jsonLd: JSON_LD, jsonLdId: "ld-rc-pro",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Breadcrumb current={{ label: "RC Professionale", href: "/rc-professionale" }} />

      <LandingHero
        eyebrow="RC Professionale · Dal 1958"
        title={<>RC Professionale, <span className="italic">cucita sulla tua attività.</span></>}
        description="Una polizza che parte dalla tua professione: categoria, fatturato, clienti, rischi reali. Massimali, retroattività e franchigie calibrati sul tuo profilo."
        imageSrc={HERO_IMG}
        imageAlt="RC Professionale — Da Pra Assicurazioni"
        ctaPrimaryLabel="Richiedi un preventivo"
        ctaPrimaryHref="/preventivo?type=altro"
        trustItems={TRUST}
      />

      <CoverageGrid
        eyebrow="Cosa include"
        heading="Le coperture per la tua professione."
        items={COVERAGE}
        cols={2}
      />

      <WhyUsSection
        eyebrow="Il nostro approccio"
        heading="Una consulenza che parte dall'ascolto."
        body={[
          "Da tre generazioni lavoriamo con professionisti, consulenti e piccole imprese. Conosciamo le specificità degli ordini professionali e i rischi tipici di ogni categoria.",
          "Il punto di partenza è sempre lo stesso: un colloquio per capire la tua attività. Solo dopo confrontiamo le condizioni e costruiamo la proposta.",
        ]}
        items={WHY}
      />

      <TestimonialsSection heading="Le voci di chi si fida di noi." />
      <FaqSection heading="Domande frequenti sulla RC Professionale." faqs={FAQS} />

      <Footer />
    </div>
  );
}
